import { PasseriniAPI } from '../../../../services/apiService';
import { CreateUserData, PasswordChangeData, UpdateUserProfileData, UserPasswordViewData } from '../types';

export interface UpdateUserProfileResponseData {
  userId: number;
  user?: {
    id: number;
    name: string;
    email?: string | null;
  };
  profile: {
    dni: string | null;
    telefono: string | null;
    direccion: string | null;
  };
}

export interface UserServiceResponse {
  success: boolean;
  message: string;
  data?: any;
}

export class UserService {
  /**
   * Get all roles for a specific user
   */
  static async getUserRoles(userId: number): Promise<string[]> {
    try {
      const response = await PasseriniAPI.get(`/users/${userId}/roles`);
      // Extract role names from the response
      return response.data.map((role: any) => role.name || role);
    } catch (error) {
      console.error('Error getting user roles:', error);
      throw new Error('Error al obtener los roles del usuario');
    }
  }

  /**
   * Sync user roles (replace all current roles with new ones)
   */
  static async syncRoles(userId: number, roles: string[]): Promise<UserServiceResponse> {
    try {
      const response = await PasseriniAPI.put(`/users/${userId}/roles/sync`, {
        roles: roles
      });
      return {
        success: true,
        message: response.data.message || 'Roles actualizados exitosamente',
        data: response.data
      };
    } catch (error: any) {
      console.error('Error syncing roles:', error);
      const message = error.response?.data?.message || 'Error al actualizar los roles';
      throw new Error(message);
    }
  }

  /**
   * Assign a single role to a user (add to existing roles)
   */
  static async assignRole(userId: number, role: string): Promise<UserServiceResponse> {
    try {
      const response = await PasseriniAPI.post(`/users/${userId}/roles/assign`, {
        role: role
      });
      return {
        success: true,
        message: response.data.message || `Rol ${role} asignado exitosamente`,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error assigning role:', error);
      const message = error.response?.data?.message || `Error al asignar el rol ${role}`;
      throw new Error(message);
    }
  }

  /**
   * Remove a single role from a user
   */
  static async removeRole(userId: number, role: string): Promise<UserServiceResponse> {
    try {
      const response = await PasseriniAPI.delete(`/users/${userId}/roles/remove`, {
        data: { role: role }
      });
      return {
        success: true,
        message: response.data.message || `Rol ${role} removido exitosamente`,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error removing role:', error);
      const message = error.response?.data?.message || `Error al remover el rol ${role}`;
      throw new Error(message);
    }
  }

  /**
   * Create a new user
   */
  static async createUser(userData: CreateUserData): Promise<UserServiceResponse> {
    try {
      const response = await PasseriniAPI.post('/users', userData);
      return {
        success: true,
        message: response.data.message || 'Usuario creado exitosamente',
        data: response.data
      };
    } catch (error: any) {
      console.error('Error creating user:', error);
      const message = error.response?.data?.message || 'Error al crear el usuario';
      throw new Error(message);
    }
  }

  /**
   * Change user password
   */
  static async changePassword(userId: number, passwordData: PasswordChangeData): Promise<UserServiceResponse> {
    try {
      const response = await PasseriniAPI.put(`/users/${userId}/password`, passwordData);
      return {
        success: true,
        message: response.data.message || 'Contraseña actualizada exitosamente',
        data: response.data
      };
    } catch (error: any) {
      console.error('Error changing password:', error);
      const message = error.response?.data?.message || 'Error al cambiar la contraseña';
      throw new Error(message);
    }
  }

  static async getVisiblePassword(userId: number): Promise<UserPasswordViewData> {
    try {
      const response = await PasseriniAPI.get<UserPasswordViewData>(`/users/${userId}/password`);
      return response.data;
    } catch (error: any) {
      console.error('Error getting visible password:', error);
      const message = error.response?.data?.message || 'Error al obtener la contraseña';
      throw new Error(message);
    }
  }

  /**
   * Update user profile personal data
   */
  static async updateUserProfile(userId: number, profileData: UpdateUserProfileData): Promise<UserServiceResponse & { data: UpdateUserProfileResponseData }> {
    try {
      const response = await PasseriniAPI.put(`/users/${userId}/profile`, profileData);
      return {
        success: true,
        message: response.data.message || 'Perfil actualizado exitosamente',
        data: response.data
      };
    } catch (error: any) {
      console.error('Error updating user profile:', error);
      const message = error.response?.data?.message || 'Error al actualizar el perfil';
      throw new Error(message);
    }
  }

  /**
   * Send credentials notification to user
   */
  static async notifyCredentials(userEmail: string): Promise<UserServiceResponse> {
    try {
      const response = await PasseriniAPI.post('/notify/credentials', {
        email: userEmail
      });
      return {
        success: true,
        message: response.data.message || 'Credenciales enviadas exitosamente',
        data: response.data
      };
    } catch (error: any) {
      console.error('Error notifying credentials:', error);
      const message = error.response?.data?.message || 'Error al enviar las credenciales';
      throw new Error(message);
    }
  }
}

export default UserService;
